import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, FingerPrintIcon, MapPinIcon, ClockIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import ActivityLogs from '../components/ActivityLogs';
import Reports from '../components/Reports';
import { biometricAPI } from '../services/employee.service';

// ====================================================================
// Mock Data Store & Constants
// ====================================================================

const initialDevices = [
    {
        id: 'BMT001',
        name: 'Main Office Fingerprint Scanner',
        type: 'ZKTecho Device',
        endpoint: 'http://192.168.1.100:8080/api/punch',
        status: 'Active',
        location: 'HQ Lobby Entrance',
    },
    {
        id: 'BMT002',
        name: 'Warehouse Face Recognition',
        type: 'Custom API',
        endpoint: 'https://api.mybiometric.com/warehouse/checkin',
        status: 'Inactive',
        location: 'Warehouse Floor',
    },
];

const initialAssignments = [
    {
        id: 'ASG001',
        device: 'Main Office Fingerprint Scanner',
        appliesTo: 'All Employees',
        location: 'HQ Lobby Entrance',
        schedule: 'Standard Working Hours',
    },
    {
        id: 'ASG002',
        device: 'Warehouse Face Recognition',
        appliesTo: 'Warehouse Staff',
        location: 'Warehouse Floor',
        schedule: '24/7 Access',
    },
];

const deviceTypeOptions = [
    'ZKTecho Device',
    'Custom API',
    'Suprema Reader',
    'Geo-Fencing (Mobile)',
];

const statusBadge = (status) => {
    const color = status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
    return <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${color}`}>{status}</span>;
};


// ====================================================================
// 1. DeviceFormModal Component (Nested)
// ====================================================================

const DeviceFormModal = ({ isOpen, onClose, onSave, device }) => {
    const isNew = useMemo(() => device === null, [device]);
    const [deviceData, setDeviceData] = useState(() => ({
        id: device?.id || null,
        name: device?.name || '',
        type: device?.type || deviceTypeOptions[0],
        endpoint: device?.endpoint || '',
        status: device?.status || 'Active',
        location: device?.location || '',
    }));

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setDeviceData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!deviceData.name.trim() || !deviceData.endpoint.trim()) {
            alert('Please fill in the device name and endpoint/IP.');
            return;
        }
        onSave(deviceData, isNew);
    };

    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel 
                                className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-2xl transition-all"
                                style={{ fontFamily: 'Roboto, sans-serif' }}
                            >
                                <Dialog.Title 
                                    as="h3" 
                                    className="text-lg font-semibold leading-6 text-gray-800 border-b pb-3 flex justify-between items-center"
                                    style={{ fontFamily: 'Poppins, sans-serif' }}
                                >
                                    {isNew ? 'Configure New Biometric Device' : `Edit Device: ${deviceData.name}`}
                                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition">
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </Dialog.Title>

                                <form onSubmit={handleSubmit} className="mt-4 space-y-4 text-sm">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="name" className="block text-xs font-medium text-gray-700">Device Name/Identifier</label>
                                            <input
                                                type="text"
                                                name="name"
                                                id="name"
                                                value={deviceData.name}
                                                onChange={handleInputChange}
                                                required
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="type" className="block text-xs font-medium text-gray-700">Device Type</label>
                                            <select
                                                name="type"
                                                id="type"
                                                value={deviceData.type}
                                                onChange={handleInputChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            >
                                                {deviceTypeOptions.map(opt => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label htmlFor="endpoint" className="block text-xs font-medium text-gray-700">API Endpoint / Device IP</label>
                                        <input
                                            type="url"
                                            name="endpoint"
                                            id="endpoint"
                                            value={deviceData.endpoint}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="e.g., http://192.168.1.100 or https://api.myservice.com/punch"
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                        />
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="location" className="block text-xs font-medium text-gray-700">Physical Location</label>
                                            <input
                                                type="text"
                                                name="location"
                                                id="location"
                                                value={deviceData.location}
                                                onChange={handleInputChange}
                                                placeholder="e.g., HQ Lobby, 2nd Floor"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="status" className="block text-xs font-medium text-gray-700">Status</label>
                                            <select
                                                name="status"
                                                id="status"
                                                value={deviceData.status}
                                                onChange={handleInputChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            >
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                                <option value="Maintenance">Maintenance</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700"
                                        >
                                            {isNew ? 'Add Device' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

// ====================================================================
// 2. AssignmentFormModal Component (Nested)
// ====================================================================

const AssignmentFormModal = ({ isOpen, onClose, onSave, assignment, availableDevices }) => {
    const isNew = useMemo(() => assignment === null, [assignment]);
    const [assignmentData, setAssignmentData] = useState(() => ({
        id: assignment?.id || null,
        device: assignment?.device || availableDevices[0] || '',
        appliesTo: assignment?.appliesTo || 'All Employees',
        location: assignment?.location || '',
        schedule: assignment?.schedule || 'Standard Working Hours',
    }));

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setAssignmentData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!assignmentData.device.trim() || !assignmentData.appliesTo.trim()) {
            alert('Please select a device and specify who it applies to.');
            return;
        }
        onSave(assignmentData, isNew);
    };

    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel 
                                className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-2xl transition-all"
                                style={{ fontFamily: 'Roboto, sans-serif' }}
                            >
                                <Dialog.Title 
                                    as="h3" 
                                    className="text-lg font-semibold leading-6 text-gray-800 border-b pb-3 flex justify-between items-center"
                                    style={{ fontFamily: 'Poppins, sans-serif' }}
                                >
                                    {isNew ? 'Create Device Assignment Rule' : `Edit Assignment: ${assignment?.appliesTo}`}
                                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition">
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </Dialog.Title>

                                <form onSubmit={handleSubmit} className="mt-4 space-y-4 text-sm">
                                    <div>
                                        <label htmlFor="device" className="block text-xs font-medium text-gray-700">Select Device</label>
                                        <select
                                            name="device"
                                            id="device"
                                            value={assignmentData.device}
                                            onChange={handleInputChange}
                                            required
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                        >
                                            {availableDevices.map(name => (
                                                <option key={name} value={name}>{name}</option>
                                            ))}
                                        </select>
                                        {availableDevices.length === 0 && (
                                            <p className="text-red-500 text-xs mt-1">Please configure Devices first.</p>
                                        )}
                                    </div>
                                    
                                    <div>
                                        <label htmlFor="appliesTo" className="block text-xs font-medium text-gray-700">Applies To (Department/Group/All)</label>
                                        <input
                                            type="text"
                                            name="appliesTo"
                                            id="appliesTo"
                                            value={assignmentData.appliesTo}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="e.g., Engineering Team, All Employees"
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                        />
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="location" className="block text-xs font-medium text-gray-700">Location (Optional)</label>
                                            <input
                                                type="text"
                                                name="location"
                                                id="location"
                                                value={assignmentData.location}
                                                onChange={handleInputChange}
                                                placeholder="e.g., HQ, Remote"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="schedule" className="block text-xs font-medium text-gray-700">Access Schedule</label>
                                            <input
                                                type="text"
                                                name="schedule"
                                                id="schedule"
                                                value={assignmentData.schedule}
                                                onChange={handleInputChange}
                                                placeholder="e.g., 24/7 Access, 9 AM - 6 PM"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700"
                                        >
                                            {isNew ? 'Create Assignment' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};


// ====================================================================
// 3. BiometricIntegrationSetup (Main Component)
// ====================================================================

const BiometricIntegrationSetup = () => {
    const [devices, setDevices] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [activeTab, setActiveTab] = useState('setup');
    const [loading, setLoading] = useState(true);
    
    // Modal State
    const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
    const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
    
    const [currentDevice, setCurrentDevice] = useState(null);
    const [currentAssignment, setCurrentAssignment] = useState(null);

    // Load devices and assignments from API
    useEffect(() => {
        loadDevices();
        loadAssignments();
    }, []);

    const loadDevices = async () => {
        try {
            setLoading(true);
            const response = await biometricAPI.listDevices();
            const data = response.data || response;
            const mappedDevices = (Array.isArray(data) ? data : []).map(device => ({
                id: device.id,
                name: device.device_name,
                type: device.device_type,
                endpoint: device.api_endpoint,
                location: device.physical_location,
                status: device.status
            }));
            setDevices(mappedDevices);
        } catch (error) {
            console.error('Failed to load devices:', error);
            setDevices([]);
        } finally {
            setLoading(false);
        }
    };

    const loadAssignments = async () => {
        try {
            const response = await biometricAPI.listAssignments();
            const data = response.data || response;
            const mappedAssignments = (Array.isArray(data) ? data : []).map(assignment => {
                const device = devices.find(d => d.id === assignment.device_id);
                return {
                    id: assignment.id,
                    device: device?.name || `Device #${assignment.device_id}`,
                    appliesTo: assignment.applies_to,
                    location: assignment.location_context,
                    schedule: assignment.access_schedule
                };
            });
            setAssignments(mappedAssignments);
        } catch (error) {
            console.error('Failed to load assignments:', error);
            setAssignments([]);
        }
    };

    // Biometric Integration specific activity types
    const activityTypes = [
        "Device Added", "Device Updated", "Device Deleted", "Device Activated", "Device Deactivated",
        "Assignment Created", "Assignment Updated", "Assignment Deleted", "Configuration Changed",
        "Biometric Data Synced", "Device Connection Test", "System Integration Updated"
    ];

    // Biometric Integration specific report types
    const reportTypes = [
        "Device Status Report", "Assignment Rules Report", "Biometric Activity Report",
        "Device Performance Report", "Integration Health Report", "Access Control Report",
        "Device Maintenance Report", "User Assignment Report"
    ];

    // Memoized list of active device names for the Assignment Modal
    const availableDevices = useMemo(() => devices.map(d => d.name), [devices]);

    // --- Device Management Handlers ---
    const handleCreateDevice = useCallback(() => {
        setCurrentDevice(null);
        setIsDeviceModalOpen(true);
    }, []);

    const handleEditDevice = useCallback((device) => {
        setCurrentDevice(device);
        setIsDeviceModalOpen(true);
    }, []);

    const handleSaveDevice = useCallback(async (newDeviceData, isNew) => {
        try {
            const payload = {
                device_name: newDeviceData.name,
                device_type: newDeviceData.type,
                api_endpoint: newDeviceData.endpoint,
                status: newDeviceData.status.toUpperCase(),
                physical_location: newDeviceData.location
            };
            
            if (isNew) {
                await biometricAPI.createDevice(payload);
            } else {
                await biometricAPI.updateDevice(newDeviceData.id, payload);
            }
            await loadDevices();
            setIsDeviceModalOpen(false);
        } catch (error) {
            console.error('Failed to save device:', error);
            const errorMsg = error.response?.data?.detail || error.message || 'Failed to save device';
            alert(`Error: ${JSON.stringify(errorMsg, null, 2)}`);
        }
    }, []);

    const handleDeleteDevice = useCallback(async (id) => {
        if (window.confirm('Are you sure you want to delete this Biometric Device? This will remove associated assignments.')) {
            try {
                await biometricAPI.deleteDevice(id);
                await loadDevices();
                await loadAssignments();
            } catch (error) {
                console.error('Failed to delete device:', error);
                const errorMsg = error.response?.status === 500 
                    ? 'Backend error: The device may have dependencies. Contact your administrator.'
                    : error.response?.data?.detail || 'Failed to delete device';
                alert(errorMsg);
            }
        }
    }, []);

    // --- Assignment Management Handlers ---
    const handleCreateAssignment = useCallback(() => {
        setCurrentAssignment(null);
        setIsAssignmentModalOpen(true);
    }, []);

    const handleEditAssignment = useCallback((assignment) => {
        setCurrentAssignment(assignment);
        setIsAssignmentModalOpen(true);
    }, []);

    const handleSaveAssignment = useCallback(async (newAssignmentData, isNew) => {
        try {
            const payload = {
                device_id: devices.find(d => d.name === newAssignmentData.device)?.id,
                applies_to: newAssignmentData.appliesTo,
                location_context: newAssignmentData.location,
                access_schedule: newAssignmentData.schedule
            };
            
            if (!payload.device_id) {
                alert('Device not found. Please select a valid device.');
                return;
            }
            
            if (isNew) {
                await biometricAPI.createAssignment(payload);
            } else {
                await biometricAPI.updateAssignment(newAssignmentData.id, payload);
            }
            await loadAssignments();
            setIsAssignmentModalOpen(false);
        } catch (error) {
            console.error('Failed to save assignment:', error);
            const errorMsg = error.response?.data?.detail || error.message || 'Failed to save assignment';
            alert(`Error: ${JSON.stringify(errorMsg, null, 2)}`);
        }
    }, [devices]);

    const handleDeleteAssignment = useCallback(async (id) => {
        if (window.confirm('Are you sure you want to delete this Assignment Rule?')) {
            try {
                await biometricAPI.deleteAssignment(id);
                await loadAssignments();
            } catch (error) {
                console.error('Failed to delete assignment:', error);
                alert('Failed to delete assignment. Please try again.');
            }
        }
    }, []);

    return (
        <div 
            className="min-h-screen p-4 sm:p-6 lg:p-8 bg-gray-50"
            style={{ fontFamily: 'Roboto, sans-serif' }}
        >
            <header className="pb-6 border-b border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Biometric & Attendance Integration Setup
                    </h1>
                </div>
                
                {/* Tab Navigation */}
                <div className="flex space-x-1">
                    <button
                        onClick={() => setActiveTab('setup')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow transition-colors ${
                            activeTab === 'setup' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                        <FingerPrintIcon className="w-4 h-4" />
                        Setup
                    </button>
                    <button
                        onClick={() => setActiveTab('activity')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow transition-colors ${
                            activeTab === 'activity' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                        <ClockIcon className="w-4 h-4" />
                        Activity Logs
                    </button>
                    <button
                        onClick={() => setActiveTab('reports')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow transition-colors ${
                            activeTab === 'reports' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                        <ChartBarIcon className="w-4 h-4" />
                        Reports
                    </button>
                </div>
            </header>

            {/* Tab Content */}
            <div className="mt-8">
                {/* Activity Logs Tab */}
                {activeTab === 'activity' && (
                    <ActivityLogs 
                        moduleName="Biometric Integration" 
                        activityTypes={activityTypes} 
                    />
                )}
                
                {/* Reports Tab */}
                {activeTab === 'reports' && (
                    <Reports 
                        moduleName="Biometric Integration" 
                        reportTypes={reportTypes}
                        devices={devices}
                        assignments={assignments}
                    />
                )}

                {/* Setup Tab */}
                {activeTab === 'setup' && (
                    <div className="space-y-12">
                        {/* ---------------------------------------------------- */}
                        {/* 1. Device/Endpoint List Section */}
                        {/* ---------------------------------------------------- */}
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-2xl font-semibold text-gray-700" style={{ fontFamily: 'Poppins, sans-serif' }}>
                                    Configured Biometric Devices ({devices.length})
                                </h2>
                                <button
                                    onClick={handleCreateDevice}
                                    className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-indigo-700 transition"
                                >
                                    <FingerPrintIcon className="w-5 h-5" />
                                    <span>Add New Device</span>
                                </button>
                            </div>

                            {/* Devices Table */}
                            <div className="bg-white shadow-lg rounded-xl overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            {['Name', 'Type', 'Endpoint/IP', 'Location', 'Status', 'Actions'].map(header => (
                                                <th
                                                    key={header}
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                    style={{ fontFamily: 'Roboto, sans-serif' }}
                                                >
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {devices.map((device) => (
                                            <tr key={device.id} className="hover:bg-gray-50 transition">
                                                <td className="px-6 py-4 font-medium text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>{device.name}</td>
                                                <td className="px-6 py-4 text-gray-700">{device.type}</td>
                                                <td className="px-6 py-4 text-gray-500 max-w-xs truncate">{device.endpoint}</td>
                                                <td className="px-6 py-4 text-gray-700 whitespace-nowrap">
                                                    <span className="flex items-center"><MapPinIcon className="w-4 h-4 mr-1 text-indigo-400"/> {device.location}</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">{statusBadge(device.status)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                                                    <button onClick={() => handleEditDevice(device)} className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50">
                                                        <PencilIcon className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={() => handleDeleteDevice(device.id)} className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50">
                                                        <TrashIcon className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {devices.length === 0 && (
                                            <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-500 italic">No biometric devices configured.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* ---------------------------------------------------- */}
                        {/* 2. Device Assignment Rules Section */}
                        {/* ---------------------------------------------------- */}
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-2xl font-semibold text-gray-700" style={{ fontFamily: 'Poppins, sans-serif' }}>
                                    Device Assignment Rules ({assignments.length})
                                </h2>
                                <button
                                    onClick={handleCreateAssignment}
                                    disabled={devices.length === 0}
                                    className={`flex items-center space-x-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md transition 
                                        ${devices.length > 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}
                                >
                                    <PlusIcon className="w-5 h-5" />
                                    <span>Add Assignment</span>
                                </button>
                            </div>

                            {/* Assignments Table */}
                            <div className="bg-white shadow-lg rounded-xl overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            {['Device', 'Applies To', 'Location Context', 'Access Schedule', 'Actions'].map(header => (
                                                <th
                                                    key={header}
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                    style={{ fontFamily: 'Roboto, sans-serif' }}
                                                >
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {assignments.map((assignment) => (
                                            <tr key={assignment.id} className="hover:bg-gray-50 transition">
                                                <td className="px-6 py-4 font-medium text-indigo-700" style={{ fontFamily: 'Poppins, sans-serif' }}>{assignment.device}</td>
                                                <td className="px-6 py-4 text-gray-700">{assignment.appliesTo}</td>
                                                <td className="px-6 py-4 text-gray-500">{assignment.location}</td>
                                                <td className="px-6 py-4 text-gray-500">{assignment.schedule}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                                                    <button onClick={() => handleEditAssignment(assignment)} className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50">
                                                        <PencilIcon className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={() => handleDeleteAssignment(assignment.id)} className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50">
                                                        <TrashIcon className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {assignments.length === 0 && (
                                            <tr><td colSpan="5" className="px-6 py-10 text-center text-gray-500 italic">No device assignment rules defined.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Device Modal */}
            {isDeviceModalOpen && (
                <DeviceFormModal
                    isOpen={isDeviceModalOpen}
                    onClose={() => setIsDeviceModalOpen(false)}
                    onSave={handleSaveDevice}
                    device={currentDevice}
                />
            )}

            {/* Assignment Modal */}
            {isAssignmentModalOpen && (
                <AssignmentFormModal
                    isOpen={isAssignmentModalOpen}
                    onClose={() => setIsAssignmentModalOpen(false)}
                    onSave={handleSaveAssignment}
                    assignment={currentAssignment}
                    availableDevices={availableDevices}
                />
            )}
        </div>
    );
};

export default BiometricIntegrationSetup;